import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CheckBox,
  Layer,
  Menu,
  RadioButtonGroup,
  RangeInput,
  Select,
  Text,
  TextArea,
  TextInput,
} from 'grommet';
import {
  Brush,
  Clipboard,
  Configure,
  Copy,
  Cursor,
  Edit,
  FormAdd,
  FormClose,
  FormSubtract,
  Layer as LayerIcon,
  MoreVertical,
  ObjectGroup,
  Pause,
  Play,
  RadialSelected,
  Refresh,
  Save,
  Target,
  Trash,
  Upload,
  Volume,
} from 'grommet-icons';
import { useTranslation } from 'react-i18next';
import { useGetMediasByCategoryQuery } from '../../api/mediasApi';
import {
  createWheelAppearanceThemeCode,
  parseWheelAppearanceThemeCode,
} from './wheelAppearance';
import { getFontOptions, loadFontStylesheets } from './fontMediaUtils';
import {
  getWheelTickSoundPreset,
  isWheelTickSoundBackground,
  wheelTickSoundPresets,
} from '../wheel/wheelSoundPresets';
import './WheelAppearanceEditor.css';

const ColorControl = ({ label, value, onChange }) => (
  <Box gap="xsmall">
    <Text size="small">{label}</Text>
    <Box direction="row" gap="xsmall" align="center">
      <input
        aria-label={label}
        className="wheel-appearance__color"
        type="color"
        value={value === 'transparent' ? '#000000' : value}
        onChange={(event) => onChange(event.target.value)}
      />
      <TextInput
        aria-label={`${label} value`}
        size="small"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Box>
  </Box>
);

export const RangeControl = ({ label, value, min, max, step = 1, onChange, suffix = '' }) => (
  <Box
    gap="xxsmall"
    onWheelCapture={(event) => event.stopPropagation()}
  >
    <Box direction="row" justify="between" align="center">
      <Text size="small">{label}</Text>
      <Text size="xsmall" color="text-weak">{value}{suffix}</Text>
    </Box>
    <RangeInput
      aria-label={label}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  </Box>
);

const SelectControl = ({ label, value, options, onChange }) => (
  <Box gap="xsmall">
    <Text size="small">{label}</Text>
    <Select
      aria-label={label}
      options={options}
      value={options.find((option) => option.value === value)}
      labelKey="label"
      onChange={({ option }) => onChange(option.value)}
    />
  </Box>
);

const getAssetMode = (asset) => asset?.url ? 'link' : 'file';

const FileControl = ({
  label,
  asset,
  accept = 'image/*,video/webm',
  urlFallbackKind = 'image',
  onChange,
  onClear,
  onUrlChange,
}) => {
  const { t } = useTranslation('wheel', { useSuspense: false });
  const [mode, setMode] = useState(getAssetMode(asset));
  const [urlValue, setUrlValue] = useState(asset?.url || '');

  useEffect(() => {
    setMode(getAssetMode(asset));
    setUrlValue(asset?.url || '');
  }, [asset]);

  const applyUrl = () => {
    if (!urlValue.trim()) {
      return;
    }

    if (typeof onUrlChange === 'function') {
      onUrlChange(urlValue.trim(), urlFallbackKind);
    }
  };

  const hint = asset?.url
    || (asset && mode === 'file' ? asset.name : '')
    || (mode === 'link' ? t('appearanceUrlHint') : t('appearanceLocalFileHint'));

  return (
    <Box gap="xsmall">
      <Text size="small">{label}</Text>
      <div className="wheel-appearance__asset-switch">
        <button
          type="button"
          className={`wheel-appearance__asset-switch-button${mode === 'file' ? ' wheel-appearance__asset-switch-button--active' : ''}`}
          onClick={() => setMode('file')}
        >
          {t('appearanceFileOption')}
        </button>
        <button
          type="button"
          className={`wheel-appearance__asset-switch-button${mode === 'link' ? ' wheel-appearance__asset-switch-button--active' : ''}`}
          onClick={() => setMode('link')}
        >
          {t('appearanceLinkOption')}
        </button>
      </div>
      {mode === 'file' ? (
        <Box direction="row" gap="xsmall" align="center">
          <label className="wheel-appearance__upload">
            <Upload size="small" />
            <span>{t('appearanceChooseFile')}</span>
            <input
              type="file"
              accept={accept}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  onChange(file);
                }
                event.target.value = '';
              }}
            />
          </label>
          {asset && (
            <Button
              plain
              a11yTitle={t('appearanceRemoveFile')}
              icon={<FormClose size="small" />}
              onClick={onClear}
            />
          )}
        </Box>
      ) : (
        <Box direction="row" gap="xsmall" align="center">
          <TextInput
            aria-label={`${label} URL`}
            placeholder={t('appearanceUrlPlaceholder')}
            value={urlValue}
            onChange={(event) => setUrlValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyUrl();
              }
            }}
          />
          <button
            type="button"
            className="wheel-appearance__link-apply"
            onClick={applyUrl}
            disabled={!urlValue.trim()}
          >
            {t('appearanceApplyLink')}
          </button>
          {asset && (
            <Button
              plain
              a11yTitle={t('appearanceRemoveFile')}
              icon={<FormClose size="small" />}
              onClick={() => {
                setUrlValue('');
                onClear();
              }}
            />
          )}
        </Box>
      )}
      <Text size="xsmall" color="text-weak">
        {hint}
      </Text>
    </Box>
  );
};

export const TickSoundControl = ({
  tickSound,
  customAsset,
  customSoundUrl,
  volume,
  updateValue,
  setMedia,
  setMediaUrl,
}) => {
  const { t } = useTranslation('wheel', { useSuspense: false });
  const previewAudioRef = useRef(null);
  const previewSourceRef = useRef(null);
  const isPreviewPlayingRef = useRef(false);
  const [previewSource, setPreviewSource] = useState(null);

  useEffect(() => () => {
    const previewAudio = previewAudioRef.current;
    previewAudioRef.current = null;
    previewSourceRef.current = null;
    isPreviewPlayingRef.current = false;
    previewAudio?.pause();
  }, []);

  const previewSound = (src) => {
    if (!src || typeof Audio === 'undefined') {
      return;
    }

    const currentAudio = previewAudioRef.current;
    if (currentAudio && previewSourceRef.current === src) {
      if (!isPreviewPlayingRef.current) {
        isPreviewPlayingRef.current = true;
        setPreviewSource(src);
        currentAudio.play().catch(() => {
          if (previewAudioRef.current === currentAudio) {
            isPreviewPlayingRef.current = false;
            setPreviewSource(null);
          }
        });
      } else {
        isPreviewPlayingRef.current = false;
        currentAudio.pause();
        setPreviewSource(null);
      }
      return;
    }

    isPreviewPlayingRef.current = false;
    currentAudio?.pause();
    const audio = new Audio(src);
    audio.volume = Number.isFinite(Number(volume)) ? Number(volume) : 0.2;
    previewAudioRef.current = audio;
    previewSourceRef.current = src;
    isPreviewPlayingRef.current = true;
    audio.onpause = () => {
      if (previewAudioRef.current === audio) {
        isPreviewPlayingRef.current = false;
        setPreviewSource(null);
      }
    };
    audio.onended = () => {
      if (previewAudioRef.current === audio) {
        previewAudioRef.current = null;
        previewSourceRef.current = null;
        isPreviewPlayingRef.current = false;
        setPreviewSource(null);
      }
    };
    setPreviewSource(src);
    audio.play().catch(() => {
      if (previewAudioRef.current === audio) {
        isPreviewPlayingRef.current = false;
        setPreviewSource(null);
      }
    });
  };

  const selectPreset = (presetId) => {
    const preset = getWheelTickSoundPreset(presetId);
    updateValue(['sounds', 'tick'], {
      source: 'preset',
      presetId: preset.id,
      isBackground: preset.isBackground,
    });
  };

  const selectSource = (source) => {
    updateValue(['sounds', 'tick', 'source'], source);
  };

  const isCustomPreviewing = Boolean(customSoundUrl) && previewSource === customSoundUrl;
  const isBackgroundSound = isWheelTickSoundBackground(tickSound);

  return (
    <Box gap="small">
      <Box gap="xxsmall">
        <Text weight="bold" size="small">
          {t(isBackgroundSound ? 'appearanceBackgroundSoundTitle' : 'appearanceTickSoundTitle')}
        </Text>
        <Text size="xsmall" color="text-weak">
          {t(isBackgroundSound ? 'appearanceTickSoundBackgroundHint' : 'appearanceTickSoundHint')}
        </Text>
      </Box>
      <div className="wheel-appearance__sound-grid">
        {wheelTickSoundPresets.map((preset) => {
          const isSelected = tickSound.source === 'preset' && tickSound.presetId === preset.id;
          const isPreviewing = previewSource === preset.src;
          return (
            <div
              className={`wheel-appearance__sound-card${isSelected ? ' wheel-appearance__sound-card--selected' : ''}`}
              key={preset.id}
            >
              <button
                type="button"
                className="wheel-appearance__sound-select"
                aria-pressed={isSelected}
                onClick={() => selectPreset(preset.id)}
              >
                <span>{t(preset.labelKey)}</span>
              </button>
              <button
                type="button"
                className="wheel-appearance__sound-preview"
                aria-label={t(
                  isPreviewing ? 'appearanceTickSoundPause' : 'appearanceTickSoundPreview',
                  { name: t(preset.labelKey) },
                )}
                aria-pressed={isPreviewing}
                onClick={() => previewSound(preset.src)}
              >
                {isPreviewing ? <Pause size="small" /> : <Play size="small" />}
              </button>
            </div>
          );
        })}
        <div
          className={`wheel-appearance__sound-card${tickSound.source === 'custom' ? ' wheel-appearance__sound-card--selected' : ''}`}
        >
          <button
            type="button"
            className="wheel-appearance__sound-select"
            aria-pressed={tickSound.source === 'custom'}
            onClick={() => selectSource('custom')}
          >
            <span>{t('appearanceTickSoundCustom')}</span>
          </button>
          <button
            type="button"
            className="wheel-appearance__sound-preview"
            aria-label={t(isCustomPreviewing ? 'appearanceTickSoundPauseCustom' : 'appearanceTickSoundPreviewCustom')}
            aria-pressed={isCustomPreviewing}
            disabled={!customSoundUrl}
            onClick={() => previewSound(customSoundUrl)}
          >
            {isCustomPreviewing ? <Pause size="small" /> : <Play size="small" />}
          </button>
        </div>
        <div
          className={`wheel-appearance__sound-card${tickSound.source === 'none' ? ' wheel-appearance__sound-card--selected' : ''}`}
        >
          <button
            type="button"
            className="wheel-appearance__sound-select"
            aria-pressed={tickSound.source === 'none'}
            onClick={() => selectSource('none')}
          >
            <span>{t('appearanceTickSoundNone')}</span>
          </button>
        </div>
      </div>
      {tickSound.source === 'custom' && (
        <Box gap="xsmall">
          <FileControl
            label={t('appearanceTickSoundCustomFile')}
            accept="audio/*"
            urlFallbackKind="audio"
            asset={customAsset}
            onChange={(file) => {
              setMedia('sound', file, 'tick');
              selectSource('custom');
            }}
            onUrlChange={(url) => {
              setMediaUrl('sound', url, 'tick', 'audio');
              selectSource('custom');
            }}
            onClear={() => {
              setMedia('sound', null, 'tick');
              selectPreset(tickSound.presetId);
            }}
          />
          <CheckBox
            toggle
            label={t('appearanceTickSoundBackground')}
            checked={isBackgroundSound}
            onChange={(event) => updateValue(
              ['sounds', 'tick', 'isBackground'],
              event.target.checked,
            )}
          />
          {!isBackgroundSound && (
            <Text size="xsmall" color="text-weak">
              {t('appearanceTickSoundCustomHint')}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

const getOptions = (t, prefix, values) => values.map((value) => {
  const translationSuffix = value
    .split('-')
    .map((part) => `${part[0].toUpperCase()}${part.slice(1)}`)
    .join('');

  return {
    value,
    label: t(`${prefix}${translationSuffix}`),
  };
});

const AppearanceSection = ({ id, activeSection, children }) => {
  if (id !== activeSection) {
    return null;
  }

  return <Box className="wheel-appearance__section">{children}</Box>;
};

export const WheelAppearanceEditor = ({
  isOpen,
  onToggle,
  showToggleButton = true,
  appearance,
  wheelData,
  themeRadius,
  updateValue,
  updateSector,
  setMedia,
  setMediaUrl,
  resetAppearance,
  theme,
  isDirty,
  onDiscardChanges,
  onSaveAsTheme,
  onUpdateTheme,
  onImportTheme,
  onRenameTheme,
  onRemoveTheme,
  initialSection = 'wheel',
  customTickSoundUrl,
  wheelSoundVolume = 0.2,
  onWheelSoundVolumeChange,
}) => {
  const { t, ready } = useTranslation('wheel', { useSuspense: false });
  const { data: fontMedias } = useGetMediasByCategoryQuery({ categoryCode: 'roulette_category_font' });
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const [activeSection, setActiveSection] = useState(initialSection);
  const [themeName, setThemeName] = useState('');
  const [themeAction, setThemeAction] = useState('');
  const [themeCode, setThemeCode] = useState('');
  const [themeMessage, setThemeMessage] = useState('');
  const [themeCodeError, setThemeCodeError] = useState('');
  const [importMode, setImportMode] = useState('new');
  const [isThemeActionInProgress, setIsThemeActionInProgress] = useState(false);

  useEffect(() => {
    loadFontStylesheets(fontMedias);
  }, [fontMedias]);

  useEffect(() => {
    if (!wheelData.some((item) => String(item.id) === selectedSectorId)) {
      setSelectedSectorId(wheelData[0] ? String(wheelData[0].id) : '');
    }
  }, [selectedSectorId, wheelData]);

  const fontOptions = useMemo(
    () => getFontOptions(fontMedias, appearance.text.fontFamily),
    [appearance.text.fontFamily, fontMedias]
  );

  if (!ready) {
    return null;
  }

  const selectedSector = wheelData.find((item) => String(item.id) === selectedSectorId);
  const sectorAppearance = appearance.sectorOverrides[selectedSectorId] || {};
  const radius = appearance.wheel.radius ?? Math.round(themeRadius * 100);
  const panelOptions = {
    colorMode: getOptions(t, 'appearanceColorMode', ['original', 'palette']),
    orientation: getOptions(t, 'appearanceOrientation', ['radial', 'tangential', 'horizontal']),
    align: getOptions(t, 'appearanceAlign', ['start', 'middle', 'end']),
    overflow: getOptions(t, 'appearanceOverflow', ['shrink-wrap', 'ellipsis', 'shrink', 'hide']),
    overlaySource: getOptions(t, 'appearanceOverlaySource', ['custom', 'none']),
    centerSource: getOptions(t, 'appearanceCenterSource', ['emote', 'custom', 'none']),
    pointerSource: getOptions(t, 'appearancePointerSource', ['default', 'custom', 'none']),
    pointerPosition: getOptions(t, 'appearancePointerPosition', ['right', 'top']),
  };
  const sections = [
    { id: 'wheel', label: t('appearanceWheelSection'), icon: <Configure size="small" /> },
    { id: 'sectors', label: t('appearanceSectorsSection'), icon: <RadialSelected size="small" /> },
    { id: 'labels', label: t('appearanceTextSection'), icon: <Edit size="small" /> },
    { id: 'overlay', label: t('appearanceOverlaySection'), icon: <LayerIcon size="small" /> },
    { id: 'center', label: t('appearanceCenterSection'), icon: <Target size="small" /> },
    { id: 'pointer', label: t('appearancePointerSection'), icon: <Cursor size="small" /> },
    { id: 'sounds', label: t('appearanceSoundSection'), icon: <Volume size="small" /> },
    { id: 'lines', label: t('appearanceLinesSection'), icon: <ObjectGroup size="small" /> },
  ];

  const updatePaletteColor = (index, color) => {
    const palette = [...appearance.sectors.palette];
    palette[index] = color;
    updateValue(['sectors', 'palette'], palette);
  };

  const handleSaveAsTheme = async () => {
    setIsThemeActionInProgress(true);
    const savedTheme = await onSaveAsTheme(themeName);
    setIsThemeActionInProgress(false);

    if (savedTheme) {
      setThemeName('');
      setThemeAction('');
    }
  };

  const handleUpdateTheme = async () => {
    setIsThemeActionInProgress(true);
    await onUpdateTheme();
    setIsThemeActionInProgress(false);
  };

  const handleRemoveTheme = async () => {
    if (theme?.type !== 'custom') {
      return;
    }

    setIsThemeActionInProgress(true);
    const wasRemoved = await onRemoveTheme();
    setIsThemeActionInProgress(false);

    if (wasRemoved) {
      setThemeAction('');
    }
  };

  const handleRenameTheme = async () => {
    if (theme?.type !== 'custom') {
      return;
    }

    setIsThemeActionInProgress(true);
    const renamedTheme = await onRenameTheme(themeName);
    setIsThemeActionInProgress(false);

    if (renamedTheme) {
      setThemeName('');
      setThemeAction('');
    }
  };

  const handleCopyTheme = async () => {
    const code = createWheelAppearanceThemeCode({
      name: theme?.name,
      wheel: theme?.wheel,
      appearance,
    });
    if (!code) {
      setThemeMessage(t('appearanceThemeCopyError'));
      return;
    }

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API is unavailable');
      }
      await navigator.clipboard.writeText(code);
      setThemeMessage(t('appearanceThemeCopied'));
    } catch {
      setThemeCode(code);
      setThemeCodeError('');
      setThemeAction('copy');
    }
  };

  const handleOpenImport = async () => {
    setThemeCode('');
    setThemeCodeError('');
    setImportMode('new');
    setThemeAction('import');

    try {
      if (navigator.clipboard?.readText) {
        const clipboardCode = await navigator.clipboard.readText();
        setThemeCode(clipboardCode);
      }
    } catch {
      // The text field remains available for a regular keyboard paste.
    }
  };

  const handleImportTheme = async () => {
    const importedTheme = parseWheelAppearanceThemeCode(themeCode);
    if (!importedTheme) {
      setThemeCodeError(t('appearanceThemeImportInvalid'));
      return;
    }

    setIsThemeActionInProgress(true);
    const savedTheme = await onImportTheme(importedTheme, importMode);
    setIsThemeActionInProgress(false);

    if (savedTheme) {
      setThemeMessage(t(
        importMode === 'replace' ? 'appearanceThemeReplaced' : 'appearanceThemeImported',
        { name: savedTheme.name },
      ));
      setThemeCode('');
      setThemeCodeError('');
      setImportMode('new');
      setThemeAction('');
    } else {
      setThemeCodeError(t('appearanceThemeImportError'));
    }
  };

  const closeThemeDialog = () => {
    if (!isThemeActionInProgress) {
      setThemeAction('');
      setThemeName('');
      setThemeCode('');
      setThemeCodeError('');
    }
  };

  const themeMenuItems = [
    {
      label: t('appearanceThemeCopy'),
      icon: <Copy size="small" />,
      onClick: handleCopyTheme,
    },
    {
      label: t('appearanceThemePaste'),
      icon: <Clipboard size="small" />,
      onClick: handleOpenImport,
    },
    {
      label: t('appearanceThemeSave'),
      icon: <FormAdd size="small" />,
      onClick: () => {
        setThemeName('');
        setThemeAction('save');
      },
    },
    ...(theme?.type === 'custom' ? [
      {
        label: t('appearanceThemeRename'),
        icon: <Edit size="small" />,
        onClick: () => {
          setThemeName(theme.name);
          setThemeAction('rename');
        },
      },
      {
        label: t('appearanceThemeDelete'),
        icon: <Trash size="small" />,
        onClick: () => setThemeAction('delete'),
      },
    ] : []),
  ];

  const themeDialogTitle = themeAction === 'rename'
    ? t('appearanceThemeRename')
    : themeAction === 'delete'
      ? t('appearanceThemeDelete')
      : themeAction === 'copy'
        ? t('appearanceThemeCopyTitle')
        : themeAction === 'import'
          ? t('appearanceThemePasteTitle')
          : t('appearanceThemeSave');

  return (
    <>
      {!isOpen && showToggleButton && (
        <Button
          className="wheel-appearance__toggle"
          icon={<Brush />}
          label={t('appearanceOpen')}
          onClick={onToggle}
          a11yTitle={t('appearanceOpen')}
        />
      )}
      {isOpen && (
        <Box
          className="wheel-appearance"
        >
          <Box
            className="wheel-appearance__header"
            direction="row"
            align="center"
            justify="between"
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
          >
            <Box>
              <Text weight="bold">{t('appearanceTitle')}</Text>
              <Text size="xsmall" color="text-weak">{t('appearanceSavedLocally')}</Text>
            </Box>
            <Button plain icon={<FormClose />} onClick={onToggle} a11yTitle={t('appearanceClose')} />
          </Box>

          <Box className="wheel-appearance__theme-bar" pad={{ horizontal: 'small', vertical: 'xsmall' }}>
            <Box direction="row" justify="between" align="center" gap="small">
              <Box gap="xxsmall" className="wheel-appearance__theme-summary">
                <Text size="xsmall" color="text-weak">{t('wheelTheme')}</Text>
                <Box direction="row" gap="xsmall" align="center">
                  <Text size="small" weight="bold" truncate>{theme?.name}</Text>
                  <Text className="wheel-appearance__theme-kind" size="xsmall">
                    {t(theme?.type === 'custom' ? 'appearanceThemeCustom' : 'appearanceThemeBuiltIn')}
                  </Text>
                </Box>
                {themeMessage && (
                  <Text size="xsmall" color={themeMessage === t('appearanceThemeCopyError') ? 'status-error' : 'status-ok'}>
                    {themeMessage}
                  </Text>
                )}
              </Box>
              <Menu
                a11yTitle={t('appearanceThemeActions')}
                icon={<MoreVertical size="small" />}
                items={themeMenuItems}
                dropAlign={{ right: 'right', top: 'bottom' }}
                disabled={isThemeActionInProgress}
              />
            </Box>
          </Box>

          {isDirty && (
            <Box
              className="wheel-appearance__changes"
              direction="row"
              justify="between"
              align="center"
              gap="small"
              pad={{ horizontal: 'small', vertical: 'xsmall' }}
            >
              <Text size="xsmall" color="status-warning">{t('appearanceThemeChanged')}</Text>
              <Box className="wheel-appearance__changes-actions" direction="row" gap="xsmall">
                <Button
                  plain
                  label={t('appearanceThemeDiscardAction')}
                  onClick={onDiscardChanges}
                  disabled={isThemeActionInProgress}
                />
                <Button
                  primary
                  icon={<Save size="small" />}
                  label={t('appearanceThemeUpdateAction')}
                  onClick={handleUpdateTheme}
                  disabled={isThemeActionInProgress}
                />
              </Box>
            </Box>
          )}

          {themeAction && (
            <Layer
              modal
              position="center"
              onEsc={closeThemeDialog}
              onClickOutside={closeThemeDialog}
            >
              <Card width="medium" background="#222222">
                <CardHeader
                  justify="between"
                  align="center"
                  pad={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'none' }}
                >
                  <Text weight="bold">{themeDialogTitle}</Text>
                  <Button
                    plain
                    icon={<FormClose size="small" />}
                    onClick={closeThemeDialog}
                    disabled={isThemeActionInProgress}
                    a11yTitle={t('appearanceCancel')}
                  />
                </CardHeader>
                <CardBody pad={{ top: 'small', left: 'medium', right: 'medium', bottom: 'medium' }}>
                  {themeAction === 'delete' ? (
                    <Text size="small" color="text-weak">
                      {t('appearanceThemeDeleteText', { name: theme?.name })}
                    </Text>
                  ) : themeAction === 'copy' || themeAction === 'import' ? (
                    <Box gap="xsmall">
                      <Text size="small" color="text-weak">
                        {t(themeAction === 'copy'
                          ? 'appearanceThemeCopyHint'
                          : 'appearanceThemePasteHint')}
                      </Text>
                      <TextArea
                        className="wheel-appearance__theme-code"
                        aria-label={t('appearanceThemeCode')}
                        value={themeCode}
                        readOnly={themeAction === 'copy'}
                        onChange={(event) => {
                          setThemeCode(event.target.value);
                          setThemeCodeError('');
                        }}
                        onFocus={(event) => {
                          if (themeAction === 'copy') {
                            event.target.select();
                          }
                        }}
                        autoFocus
                      />
                      {themeAction === 'import' && (
                        <RadioButtonGroup
                          aria-label={t('appearanceThemeImportMode')}
                          name="wheel-theme-import-mode"
                          options={[
                            {
                              label: t('appearanceThemeImportAsNew'),
                              value: 'new',
                            },
                            {
                              label: t('appearanceThemeImportReplace'),
                              value: 'replace',
                            },
                          ]}
                          value={importMode}
                          onChange={(event) => setImportMode(event.target.value)}
                        />
                      )}
                      <Text size="xsmall" color={themeCodeError ? 'status-error' : 'text-weak'}>
                        {themeCodeError || t('appearanceThemeMediaHint')}
                      </Text>
                    </Box>
                  ) : (
                    <Box gap="xsmall">
                      <Text size="small">{t('appearanceThemeName')}</Text>
                      <TextInput
                        aria-label={t('appearanceThemeName')}
                        placeholder={t('appearanceThemeNamePlaceholder')}
                        value={themeName}
                        onChange={(event) => setThemeName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') {
                            return;
                          }
                          event.preventDefault();
                          if (themeAction === 'rename') {
                            handleRenameTheme();
                          } else {
                            handleSaveAsTheme();
                          }
                        }}
                        autoFocus
                      />
                    </Box>
                  )}
                </CardBody>
                <CardFooter justify="end" gap="small" pad={{ top: 'none', left: 'medium', right: 'medium', bottom: 'medium' }}>
                  <Button
                    label={t('appearanceCancel')}
                    onClick={closeThemeDialog}
                    disabled={isThemeActionInProgress}
                  />
                  {themeAction !== 'copy' && (
                    <Button
                      primary
                      label={themeAction === 'delete'
                        ? t('appearanceThemeDeleteAction')
                        : themeAction === 'rename'
                          ? t('appearanceThemeSaveAction')
                          : themeAction === 'import'
                            ? t('appearanceThemeImportAction')
                            : t('appearanceThemeCreateAction')}
                      onClick={themeAction === 'delete'
                        ? handleRemoveTheme
                        : themeAction === 'rename'
                          ? handleRenameTheme
                          : themeAction === 'import'
                            ? handleImportTheme
                            : handleSaveAsTheme}
                      disabled={isThemeActionInProgress
                        || (themeAction === 'import' ? !themeCode.trim() : themeAction !== 'delete' && !themeName.trim())}
                    />
                  )}
                </CardFooter>
              </Card>
            </Layer>
          )}

          <div className="wheel-appearance__body">
            <nav className="wheel-appearance__navigation">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className={`wheel-appearance__navigation-button${activeSection === section.id ? ' wheel-appearance__navigation-button--active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                  aria-current={activeSection === section.id ? 'page' : undefined}
                >
                  {section.icon}
                  <span>{section.label}</span>
                </button>
              ))}
            </nav>
            <div className="wheel-appearance__content">
              <AppearanceSection id="wheel" activeSection={activeSection}>
                <Box pad="small" gap="small">
                  <ColorControl
                    label={t('appearanceWheelBackground')}
                    value={appearance.wheel.background}
                    onChange={(value) => updateValue(['wheel', 'background'], value)}
                  />
                  <RangeControl
                    label={t('appearanceWheelRadius')}
                    value={radius}
                    min={65}
                    max={100}
                    onChange={(value) => updateValue(['wheel', 'radius'], value)}
                    suffix="%"
                  />
                  <CheckBox
                    toggle
                    label={t('appearanceDebugMode')}
                    checked={appearance.wheel.debug}
                    onChange={(event) => updateValue(['wheel', 'debug'], event.target.checked)}
                  />
                  <CheckBox
                    toggle
                    label={t('appearanceIdleAnimation')}
                    checked={appearance.idleAnimation.enabled}
                    onChange={(event) => updateValue(['idleAnimation', 'enabled'], event.target.checked)}
                  />
                  {appearance.idleAnimation.enabled && (
                    <>
                      <RangeControl
                        label={t('appearanceAnimationDuration')}
                        value={appearance.idleAnimation.duration}
                        min={500}
                        max={10000}
                        step={100}
                        onChange={(value) => updateValue(['idleAnimation', 'duration'], value)}
                        suffix=" ms"
                      />
                      <RangeControl
                        label={t('appearanceAnimationRotation')}
                        value={appearance.idleAnimation.rotation}
                        min={0}
                        max={5}
                        step={0.05}
                        onChange={(value) => updateValue(['idleAnimation', 'rotation'], value)}
                        suffix="°"
                      />
                      <RangeControl
                        label={t('appearanceAnimationScale')}
                        value={appearance.idleAnimation.scale}
                        min={0.95}
                        max={1.08}
                        step={0.01}
                        onChange={(value) => updateValue(['idleAnimation', 'scale'], value)}
                      />
                    </>
                  )}
                </Box>
              </AppearanceSection>

              <AppearanceSection id="sectors" activeSection={activeSection}>
                <Box pad="small" gap="small">
                  <SelectControl
                    label={t('appearanceSectorColors')}
                    value={appearance.sectors.colorMode}
                    options={panelOptions.colorMode}
                    onChange={(value) => updateValue(['sectors', 'colorMode'], value)}
                  />
                  {appearance.sectors.colorMode === 'palette' && (
                    <Box gap="xsmall">
                      <Text size="small">{t('appearancePalette')}</Text>
                      <Box direction="row" wrap gap="xsmall">
                        {appearance.sectors.palette.map((color, index) => (
                          <input
                            key={`${index}-${color}`}
                            aria-label={`${t('appearancePalette')} ${index + 1}`}
                            className="wheel-appearance__color wheel-appearance__color--swatch"
                            type="color"
                            value={color}
                            onChange={(event) => updatePaletteColor(index, event.target.value)}
                          />
                        ))}
                        <Button
                          plain
                          icon={<FormAdd size="small" />}
                          a11yTitle={t('appearanceAddColor')}
                          onClick={() => updateValue(
                            ['sectors', 'palette'],
                            [...appearance.sectors.palette, '#01a982'],
                          )}
                        />
                        {appearance.sectors.palette.length > 1 && (
                          <Button
                            plain
                            icon={<FormSubtract size="small" />}
                            a11yTitle={t('appearanceRemoveColor')}
                            onClick={() => updateValue(
                              ['sectors', 'palette'],
                              appearance.sectors.palette.slice(0, -1),
                            )}
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {selectedSector && (
                    <>
                      <SelectControl
                        label={t('appearanceSelectedSector')}
                        value={selectedSectorId}
                        options={wheelData.map((item) => ({
                          value: String(item.id),
                          label: item.lotName,
                        }))}
                        onChange={setSelectedSectorId}
                      />
                      <ColorControl
                        label={t('appearanceSectorColor')}
                        value={sectorAppearance.color || selectedSector.backgroundColor || '#01a982'}
                        onChange={(value) => updateSector(selectedSectorId, 'color', value)}
                      />
                      <ColorControl
                        label={t('appearanceSectorTextColor')}
                        value={sectorAppearance.textColor || selectedSector.labelColor || appearance.text.color}
                        onChange={(value) => updateSector(selectedSectorId, 'textColor', value)}
                      />
                      <FileControl
                        label={t('appearanceSectorImage')}
                        accept="image/*"
                        asset={appearance.media.sectors[selectedSectorId]}
                        onChange={(file) => setMedia('sector', file, selectedSectorId)}
                        onUrlChange={(url) => setMediaUrl('sector', url, selectedSectorId, 'image')}
                        onClear={() => setMedia('sector', null, selectedSectorId)}
                      />
                      {(appearance.media.sectors[selectedSectorId] || selectedSector.image) && (
                        <>
                          <SelectControl
                            label={t('appearanceImageFit')}
                            value={sectorAppearance.imageFit || 'cover'}
                            options={getOptions(t, 'appearanceImageFit', ['cover', 'contain', 'stretch'])}
                            onChange={(value) => updateSector(selectedSectorId, 'imageFit', value)}
                          />
                          <RangeControl
                            label={t('appearanceOpacity')}
                            value={sectorAppearance.imageOpacity ?? 1}
                            min={0}
                            max={1}
                            step={0.05}
                            onChange={(value) => updateSector(selectedSectorId, 'imageOpacity', value)}
                          />
                          <RangeControl
                            label={t('appearanceScale')}
                            value={sectorAppearance.imageScale ?? 1}
                            min={0.1}
                            max={3}
                            step={0.05}
                            onChange={(value) => updateSector(selectedSectorId, 'imageScale', value)}
                          />
                          <RangeControl
                            label={t('appearanceRotation')}
                            value={sectorAppearance.imageRotation ?? 0}
                            min={-180}
                            max={180}
                            onChange={(value) => updateSector(selectedSectorId, 'imageRotation', value)}
                            suffix="°"
                          />
                          <RangeControl
                            label={t('appearanceOffsetX')}
                            value={sectorAppearance.imageOffsetX ?? 0}
                            min={-50}
                            max={50}
                            onChange={(value) => updateSector(selectedSectorId, 'imageOffsetX', value)}
                          />
                          <RangeControl
                            label={t('appearanceOffsetY')}
                            value={sectorAppearance.imageOffsetY ?? 0}
                            min={-50}
                            max={50}
                            onChange={(value) => updateSector(selectedSectorId, 'imageOffsetY', value)}
                          />
                        </>
                      )}
                    </>
                  )}
                  {!selectedSector && <Text size="small" color="text-weak">{t('appearanceNoSectors')}</Text>}
                </Box>
              </AppearanceSection>

              <AppearanceSection id="labels" activeSection={activeSection}>
                <Box pad="small" gap="small">
                  <ColorControl
                    label={t('appearanceTextColor')}
                    value={appearance.text.color}
                    onChange={(value) => updateValue(['text', 'color'], value)}
                  />
                  <Box gap="xsmall">
                    <Text size="small">{t('appearanceFontFamily')}</Text>
                    <Select
                      aria-label={t('appearanceFontFamily')}
                      options={fontOptions}
                      value={fontOptions.find((option) => option.name === appearance.text.fontFamily)}
                      labelKey="name"
                      valueLabel={(option) => (
                        <Text style={{ padding: '6px 12px', fontFamily: `"${option.name}", sans-serif` }}>
                          {option.name}
                        </Text>
                      )}
                      onChange={({ option }) => updateValue(['text', 'fontFamily'], option.name)}
                    >
                      {(option) => (
                        <Box pad="small">
                          <Text style={{ fontFamily: `"${option.name}", sans-serif` }}>
                            {option.name}
                          </Text>
                        </Box>
                      )}
                    </Select>
                  </Box>
                  <RangeControl
                    label={t('appearanceFontSize')}
                    value={appearance.text.fontSize}
                    min={0.5}
                    max={12}
                    step={0.1}
                    onChange={(value) => updateValue(['text', 'fontSize'], value)}
                  />
                  <RangeControl
                    label={t('appearanceFontWeight')}
                    value={appearance.text.fontWeight}
                    min={100}
                    max={900}
                    step={100}
                    onChange={(value) => updateValue(['text', 'fontWeight'], value)}
                  />
                  <RangeControl
                    label={t('appearanceTextRadius')}
                    value={appearance.text.radius}
                    min={0.1}
                    max={0.95}
                    step={0.01}
                    onChange={(value) => updateValue(['text', 'radius'], value)}
                  />
                  <RangeControl
                    label={t('appearanceTextInnerRadius')}
                    value={appearance.text.innerRadius}
                    min={0}
                    max={0.95}
                    step={0.01}
                    onChange={(value) => updateValue(['text', 'innerRadius'], value)}
                  />
                  <RangeControl
                    label={t('appearanceOffsetX')}
                    value={appearance.text.offsetX}
                    min={-30}
                    max={30}
                    step={0.5}
                    onChange={(value) => updateValue(['text', 'offsetX'], value)}
                  />
                  <RangeControl
                    label={t('appearanceOffsetY')}
                    value={appearance.text.offsetY}
                    min={-30}
                    max={30}
                    step={0.5}
                    onChange={(value) => updateValue(['text', 'offsetY'], value)}
                  />
                  <SelectControl
                    label={t('appearanceOrientation')}
                    value={appearance.text.orientation}
                    options={panelOptions.orientation}
                    onChange={(value) => updateValue(['text', 'orientation'], value)}
                  />
                  <SelectControl
                    label={t('appearanceAlignment')}
                    value={appearance.text.align}
                    options={panelOptions.align}
                    onChange={(value) => updateValue(['text', 'align'], value)}
                  />
                  <SelectControl
                    label={t('appearanceOverflow')}
                    value={appearance.text.overflow}
                    options={panelOptions.overflow}
                    onChange={(value) => updateValue(['text', 'overflow'], value)}
                  />
                  {appearance.text.overflow === 'shrink-wrap' && (
                    <>
                      <RangeControl
                        label={t('appearanceMinFontSize')}
                        value={appearance.text.minFontSize}
                        min={0.5}
                        max={12}
                        step={0.1}
                        onChange={(value) => updateValue(['text', 'minFontSize'], value)}
                      />
                      <RangeControl
                        label={t('appearanceMaxLines')}
                        value={appearance.text.maxLines}
                        min={1}
                        max={6}
                        onChange={(value) => updateValue(['text', 'maxLines'], value)}
                      />
                    </>
                  )}
                  <ColorControl
                    label={t('appearanceStrokeColor')}
                    value={appearance.text.strokeColor}
                    onChange={(value) => updateValue(['text', 'strokeColor'], value)}
                  />
                  <RangeControl
                    label={t('appearanceStrokeWidth')}
                    value={appearance.text.strokeWidth}
                    min={0}
                    max={2}
                    step={0.05}
                    onChange={(value) => updateValue(['text', 'strokeWidth'], value)}
                  />
                  <ColorControl
                    label={t('appearanceShadowColor')}
                    value={appearance.text.shadow.color}
                    onChange={(value) => updateValue(['text', 'shadow', 'color'], value)}
                  />
                  <RangeControl
                    label={t('appearanceShadowBlur')}
                    value={appearance.text.shadow.blur}
                    min={0}
                    max={5}
                    step={0.1}
                    onChange={(value) => updateValue(['text', 'shadow', 'blur'], value)}
                  />
                  <RangeControl
                    label={t('appearanceShadowOffsetX')}
                    value={appearance.text.shadow.offsetX}
                    min={-10}
                    max={10}
                    step={0.1}
                    onChange={(value) => updateValue(['text', 'shadow', 'offsetX'], value)}
                  />
                  <RangeControl
                    label={t('appearanceShadowOffsetY')}
                    value={appearance.text.shadow.offsetY}
                    min={-10}
                    max={10}
                    step={0.1}
                    onChange={(value) => updateValue(['text', 'shadow', 'offsetY'], value)}
                  />
                </Box>
              </AppearanceSection>

              <AppearanceSection id="overlay" activeSection={activeSection}>
                <Box pad="small" gap="small">
                  <SelectControl
                    label={t('appearanceSource')}
                    value={appearance.overlay.source}
                    options={panelOptions.overlaySource}
                    onChange={(value) => updateValue(['overlay', 'source'], value)}
                  />
                  <FileControl
                    label={t('appearanceOverlayFile')}
                    asset={appearance.media.overlay}
                    onChange={(file) => {
                      setMedia('overlay', file);
                      updateValue(['overlay', 'source'], 'custom');
                    }}
                    onUrlChange={(url) => {
                      setMediaUrl('overlay', url, undefined, 'image');
                      updateValue(['overlay', 'source'], 'custom');
                    }}
                    onClear={() => setMedia('overlay', null)}
                  />
                  <RangeControl
                    label={t('appearanceOpacity')}
                    value={appearance.overlay.opacity}
                    min={0}
                    max={1}
                    step={0.05}
                    onChange={(value) => updateValue(['overlay', 'opacity'], value)}
                  />
                  <RangeControl
                    label={t('appearanceScale')}
                    value={appearance.overlay.scale}
                    min={0.5}
                    max={1.5}
                    step={0.01}
                    onChange={(value) => updateValue(['overlay', 'scale'], value)}
                  />
                </Box>
              </AppearanceSection>

              <AppearanceSection id="center" activeSection={activeSection}>
                <Box pad="small" gap="small">
                  <SelectControl
                    label={t('appearanceSource')}
                    value={appearance.center.source}
                    options={panelOptions.centerSource}
                    onChange={(value) => updateValue(['center', 'source'], value)}
                  />
                  {appearance.center.source !== 'emote' && (
                    <CheckBox
                      label={t('appearanceCenterShowEmote')}
                      checked={appearance.center.showEmote}
                      onChange={(event) => updateValue(['center', 'showEmote'], event.target.checked)}
                    />
                  )}
                  <FileControl
                    label={t('appearanceCenterFile')}
                    asset={appearance.media.center}
                    onChange={(file) => {
                      setMedia('center', file);
                      updateValue(['center', 'source'], 'custom');
                    }}
                    onUrlChange={(url) => {
                      setMediaUrl('center', url, undefined, 'image');
                      updateValue(['center', 'source'], 'custom');
                    }}
                    onClear={() => setMedia('center', null)}
                  />
                  <RangeControl
                    label={t('appearanceCenterSize')}
                    value={appearance.center.size}
                    min={0}
                    max={60}
                    onChange={(value) => updateValue(['center', 'size'], value)}
                    suffix="%"
                  />
                  <RangeControl
                    label={t('appearanceOpacity')}
                    value={appearance.center.opacity}
                    min={0}
                    max={1}
                    step={0.05}
                    onChange={(value) => updateValue(['center', 'opacity'], value)}
                  />
                  <RangeControl
                    label={t('appearanceScale')}
                    value={appearance.center.scale}
                    min={0.2}
                    max={2}
                    step={0.05}
                    onChange={(value) => updateValue(['center', 'scale'], value)}
                  />
                  <RangeControl
                    label={t('appearanceCornerRadius')}
                    value={appearance.center.borderRadius}
                    min={0}
                    max={50}
                    onChange={(value) => updateValue(['center', 'borderRadius'], value)}
                    suffix="%"
                  />
                  <ColorControl
                    label={t('appearanceCenterBackground')}
                    value={appearance.center.background}
                    onChange={(value) => updateValue(['center', 'background'], value)}
                  />
                  <ColorControl
                    label={t('appearanceBorderColor')}
                    value={appearance.center.borderColor}
                    onChange={(value) => updateValue(['center', 'borderColor'], value)}
                  />
                  <RangeControl
                    label={t('appearanceBorderWidth')}
                    value={appearance.center.borderWidth}
                    min={0}
                    max={12}
                    step={0.5}
                    onChange={(value) => updateValue(['center', 'borderWidth'], value)}
                    suffix=" px"
                  />
                </Box>
              </AppearanceSection>

              <AppearanceSection id="pointer" activeSection={activeSection}>
                <Box pad="small" gap="small">
                  <SelectControl
                    label={t('appearanceSource')}
                    value={appearance.pointer.source}
                    options={panelOptions.pointerSource}
                    onChange={(value) => updateValue(['pointer', 'source'], value)}
                  />
                  <SelectControl
                    label={t('appearancePointerPositionLabel')}
                    value={appearance.pointer.position}
                    options={panelOptions.pointerPosition}
                    onChange={(value) => updateValue(['pointer', 'position'], value)}
                  />
                  <FileControl
                    label={t('appearancePointerFile')}
                    asset={appearance.media.pointer}
                    onChange={(file) => {
                      setMedia('pointer', file);
                      updateValue(['pointer', 'source'], 'custom');
                    }}
                    onUrlChange={(url) => {
                      setMediaUrl('pointer', url, undefined, 'image');
                      updateValue(['pointer', 'source'], 'custom');
                    }}
                    onClear={() => setMedia('pointer', null)}
                  />
                  <ColorControl
                    label={t('appearancePointerColor')}
                    value={appearance.pointer.color}
                    onChange={(value) => updateValue(['pointer', 'color'], value)}
                  />
                  <RangeControl
                    label={t('appearancePointerSize')}
                    value={appearance.pointer.size}
                    min={3}
                    max={35}
                    onChange={(value) => updateValue(['pointer', 'size'], value)}
                    suffix="%"
                  />
                  <RangeControl
                    label={t('appearancePointerOffset')}
                    value={appearance.pointer.offset}
                    min={-15}
                    max={15}
                    onChange={(value) => updateValue(['pointer', 'offset'], value)}
                    suffix="%"
                  />
                  <RangeControl
                    label={t('appearanceOpacity')}
                    value={appearance.pointer.opacity}
                    min={0}
                    max={1}
                    step={0.05}
                    onChange={(value) => updateValue(['pointer', 'opacity'], value)}
                  />
                  <RangeControl
                    label={t('appearanceScale')}
                    value={appearance.pointer.scale}
                    min={0.2}
                    max={2}
                    step={0.05}
                    onChange={(value) => updateValue(['pointer', 'scale'], value)}
                  />
                </Box>
              </AppearanceSection>

              <AppearanceSection id="sounds" activeSection={activeSection}>
                <Box pad="small" gap="medium">
                  <Text size="small" color="text-weak">
                    {t('appearanceSoundHint')}
                  </Text>
                  <TickSoundControl
                    tickSound={appearance.sounds.tick}
                    customAsset={appearance.media.sounds.tick}
                    customSoundUrl={customTickSoundUrl}
                    volume={wheelSoundVolume}
                    updateValue={updateValue}
                    setMedia={setMedia}
                    setMediaUrl={setMediaUrl}
                  />
                  {typeof onWheelSoundVolumeChange === 'function' && (
                    <RangeControl
                      label={t('wheelSoundVolume')}
                      value={wheelSoundVolume}
                      min={0}
                      max={1}
                      step={0.05}
                      onChange={onWheelSoundVolumeChange}
                    />
                  )}
                  <Box className="wheel-appearance__sound-additional" gap="small">
                    <Text weight="bold" size="small">
                      {t('appearanceAdditionalSounds')}
                    </Text>
                    <FileControl
                      label={t('appearanceStartSound')}
                      accept="audio/*"
                      urlFallbackKind="audio"
                      asset={appearance.media.sounds.start}
                      onChange={(file) => setMedia('sound', file, 'start')}
                      onUrlChange={(url) => setMediaUrl('sound', url, 'start', 'audio')}
                      onClear={() => setMedia('sound', null, 'start')}
                    />
                    <FileControl
                      label={t('appearanceWinSound')}
                      accept="audio/*"
                      urlFallbackKind="audio"
                      asset={appearance.media.sounds.win}
                      onChange={(file) => setMedia('sound', file, 'win')}
                      onUrlChange={(url) => setMediaUrl('sound', url, 'win', 'audio')}
                      onClear={() => setMedia('sound', null, 'win')}
                    />
                    <Text size="xsmall" color="text-weak">
                      {t('appearanceWinSoundHint')}
                    </Text>
                  </Box>
                </Box>
              </AppearanceSection>

              <AppearanceSection id="lines" activeSection={activeSection}>
                <Box pad="small" gap="medium">
                  <Box gap="small">
                    <Text weight="bold" size="small">{t('appearanceOuterBorder')}</Text>
                    <ColorControl
                      label={t('appearanceBorderColor')}
                      value={appearance.border.color}
                      onChange={(value) => updateValue(['border', 'color'], value)}
                    />
                    <RangeControl
                      label={t('appearanceBorderWidth')}
                      value={appearance.border.width}
                      min={0}
                      max={5}
                      step={0.05}
                      onChange={(value) => updateValue(['border', 'width'], value)}
                    />
                    <ColorControl
                      label={t('appearanceShadowColor')}
                      value={appearance.border.shadow.color}
                      onChange={(value) => updateValue(['border', 'shadow', 'color'], value)}
                    />
                    <RangeControl
                      label={t('appearanceShadowBlur')}
                      value={appearance.border.shadow.blur}
                      min={0}
                      max={5}
                      step={0.1}
                      onChange={(value) => updateValue(['border', 'shadow', 'blur'], value)}
                    />
                    <RangeControl
                      label={t('appearanceShadowOffsetX')}
                      value={appearance.border.shadow.offsetX}
                      min={-10}
                      max={10}
                      step={0.1}
                      onChange={(value) => updateValue(['border', 'shadow', 'offsetX'], value)}
                    />
                    <RangeControl
                      label={t('appearanceShadowOffsetY')}
                      value={appearance.border.shadow.offsetY}
                      min={-10}
                      max={10}
                      step={0.1}
                      onChange={(value) => updateValue(['border', 'shadow', 'offsetY'], value)}
                    />
                  </Box>
                  <Box gap="small">
                    <Text weight="bold" size="small">{t('appearanceDividers')}</Text>
                    <ColorControl
                      label={t('appearanceBorderColor')}
                      value={appearance.dividers.color}
                      onChange={(value) => updateValue(['dividers', 'color'], value)}
                    />
                    <RangeControl
                      label={t('appearanceBorderWidth')}
                      value={appearance.dividers.width}
                      min={0}
                      max={5}
                      step={0.05}
                      onChange={(value) => updateValue(['dividers', 'width'], value)}
                    />
                    <ColorControl
                      label={t('appearanceShadowColor')}
                      value={appearance.dividers.shadow.color}
                      onChange={(value) => updateValue(['dividers', 'shadow', 'color'], value)}
                    />
                    <RangeControl
                      label={t('appearanceShadowBlur')}
                      value={appearance.dividers.shadow.blur}
                      min={0}
                      max={5}
                      step={0.1}
                      onChange={(value) => updateValue(['dividers', 'shadow', 'blur'], value)}
                    />
                    <RangeControl
                      label={t('appearanceShadowOffsetX')}
                      value={appearance.dividers.shadow.offsetX}
                      min={-10}
                      max={10}
                      step={0.1}
                      onChange={(value) => updateValue(['dividers', 'shadow', 'offsetX'], value)}
                    />
                    <RangeControl
                      label={t('appearanceShadowOffsetY')}
                      value={appearance.dividers.shadow.offsetY}
                      min={-10}
                      max={10}
                      step={0.1}
                      onChange={(value) => updateValue(['dividers', 'shadow', 'offsetY'], value)}
                    />
                  </Box>
                </Box>
              </AppearanceSection>
            </div>
          </div>

          <Box pad="small" border={{ side: 'top', color: 'border-weak' }}>
            <Button
              secondary
              icon={<Refresh size="small" />}
              label={t('appearanceReset')}
              onClick={resetAppearance}
            />
          </Box>
        </Box>
      )}
    </>
  );
};
